import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function About() {
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Hero */}
      <section className="py-16 px-6 border-b border-[#E7E5E4] bg-white">
        <div className="max-w-screen-xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373]">About the author</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#1C1C1C] leading-tight tracking-tight">
              Independent. Research-based. Not selling you anything.
            </h1>
            <blockquote className="border-l-4 border-[#D4A373] pl-5 text-[#57534E] italic text-lg leading-relaxed">
              "Every article on this site has to pass one test: would I send this to my own sister if she just landed in Canada?"
            </blockquote>
            <p className="text-[#57534E] text-sm font-medium">— Michael, founder · Mortgage associate & consultant</p>
          </div>
          <div className="rounded-xl overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_money-talk-michael/artifacts/38j7m4ke_Michael%27s%20photo.png"
              alt="Michael — Straight Talk Money"
              className="w-full h-80 lg:h-96 object-cover rounded-xl"
              onError={(e) => { e.target.src = "https://images.pexels.com/photos/4319926/pexels-photo-4319926.jpeg"; }}
            />
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 px-6 border-b border-[#E7E5E4]">
        <div className="max-w-screen-md mx-auto space-y-8 text-[#3D3D3D] leading-relaxed">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373] mb-4">The story</p>
            <p className="text-lg leading-relaxed">
              I'm Michael — a mortgage associate and consultant who got tired of watching newcomers and Canadian families get sold instead of helped.
            </p>
          </div>
          <p>
            I started Straight Talk Money to be the place where the numbers come first, the rules are explained plainly, and nobody is trying to upsell you a product on the way out. No sponsored content. No affiliate links dressed up as advice. Just the honest math.
          </p>
          <p>
            Every topic on this site — budgeting, credit, mortgages, insurance, cost of living — comes from conversations I've had with real people navigating real Canadian financial decisions. The articles are written to be the thing I wish existed when my own family members arrived in Canada and started asking questions.
          </p>
          <p>
            I'm a mortgage associate, which means I spend my days inside the actual lending process: reading income documents, running stress tests, explaining qualification rules to first-time buyers. That practical knowledge lives in everything on this site.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-6 bg-[#F2EFE9] border-b border-[#E7E5E4]">
        <div className="max-w-screen-xl mx-auto grid grid-cols-3 gap-8 text-center">
          {[
            { num: "6", label: "Pillar topics" },
            { num: "40+", label: "Research-based articles" },
            { num: "0", label: "Sponsored upsells" },
          ].map((s) => (
            <div key={s.label} className="space-y-1">
              <p className="font-bold text-3xl sm:text-4xl text-[#1B4332]">{s.num}</p>
              <p className="text-[#57534E] text-sm">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Principles */}
      <section className="py-16 px-6 bg-white border-b border-[#E7E5E4]">
        <div className="max-w-screen-xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373] mb-8">Editorial principles</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Canadian numbers only", desc: "No US-imported advice. Every rule, rate, and figure on this site is specific to Canada." },
              { title: "Research first", desc: "Every claim is backed by a source: Statistics Canada, CMHC, financial regulator guidelines, or verified lender criteria." },
              { title: "No conflicts of interest", desc: "No affiliate links. No sponsored content. No product referrals embedded in articles." },
              { title: "Plain English", desc: "Financial jargon is explained the first time it appears. Nothing is written to sound impressive — it's written to be useful." },
              { title: "Regular updates", desc: "Rates and rules change. Articles are reviewed and updated whenever a significant change affects the advice." },
              { title: "Reader-shaped content", desc: "Questions from real readers shape what gets written next. The most common stumbling blocks get the most detailed coverage." },
            ].map((p) => (
              <div key={p.title} className="border border-[#E7E5E4] rounded-lg p-6">
                <h3 className="font-bold text-[#1C1C1C] mb-2">{p.title}</h3>
                <p className="text-[#57534E] text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 text-center">
        <div className="max-w-xl mx-auto space-y-5">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1C1C1C]">Have a question or story to share?</h2>
          <p className="text-[#57534E] leading-relaxed">
            Real questions from real readers shape what gets written next. I read every message.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="about-contact-link"
              className="bg-[#1B4332] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#2D6A4F] transition-colors flex items-center gap-2"
            >
              Get in touch <ArrowRight size={14} />
            </Link>
            <Link
              to="/articles"
              className="border border-[#1B4332] text-[#1B4332] font-semibold px-6 py-3 rounded-md hover:bg-[#1B4332] hover:text-white transition-colors"
            >
              Read the articles
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
